import type React from "react";
import {Skeleton, Stack, Typography} from "@mui/material";
import {useSupplierFirmographicsQuery} from "~/pages/supplier-360/hooks/useSupplierFirmographicsQuery";
import {useSupplierExtLinkQuery} from "~/pages/supplier-360/hooks/useSupplierExtLinkQuery";
import {useRevenueShareQuery} from "~/pages/supplier-360/hooks/useRevenueShareQuery";
import {KpiStrip, type KpiTone} from "~/pages/supplier-360/components/pdl/KpiStrip";
import {SnapshotMeta} from "~/pages/supplier-360/components/pdl/SnapshotMeta";
import {MatchReviewBanner} from "~/pages/supplier-360/components/pdl/MatchReviewBanner";
import {BAND_LABEL, CHURN_WARN_PCT, formatCount, formatPct, formatShare, growthColor, hasReviewNotice, NO_VALUE} from "~/pages/supplier-360/components/pdl/pdlFormat";

const GROWTH_TONE: Record<string, KpiTone> = {error: 'bad', warning: 'warn', success: 'good', default: 'neutral'};

/**
 * Insights "Workforce & momentum": headcount, 12-month growth, churn, active job postings and share
 * of revenue from the entity's latest firmographics snapshot. No month-by-month chart, leadership
 * changes or role mix — the backend only stores point-in-time snapshots (no
 * PdlCompanySeries/PdlCompanyBreakdown-equivalent data), not a workforce history. Hidden entirely
 * once loaded with nothing to show (no data and no review notice to flag).
 */
export const WorkforceBlock: React.FC<{entityId: number}> = ({entityId}) => {
    const {data: firmographics, isLoading} = useSupplierFirmographicsQuery(entityId);
    const {data: link, isLoading: linkLoading} = useSupplierExtLinkQuery(entityId);
    const {data: share} = useRevenueShareQuery(entityId);

    const hasWorkforceData = firmographics && (
        firmographics.employee_count !== null || firmographics.growth_12m_pct !== null
        || firmographics.churn_12m_pct !== null || firmographics.active_job_postings !== null
    );
    // Both queries must have settled before deciding there's nothing to show — judging `link` while
    // it's still in flight (hasReviewNotice(undefined) === false) hides the block, then remounts it
    // the moment that second request resolves.
    if (!isLoading && !linkLoading && !hasWorkforceData && !hasReviewNotice(link)) return null;

    return (
        <Stack gap={1.5} sx={{mt: 3}}>
            <Typography variant="h6" fontWeight={600} fontSize={15}>Workforce &amp; momentum</Typography>
            {isLoading && <Skeleton variant="rectangular" height={80}/>}
            {!isLoading && <MatchReviewBanner link={link}/>}
            {hasWorkforceData && firmographics && (
                <>
                    <KpiStrip cells={[
                        {label: 'Employees', value: formatCount(firmographics.employee_count)},
                        {label: '12-mo growth', value: formatPct(firmographics.growth_12m_pct, {signed: true}),
                            tone: GROWTH_TONE[growthColor(firmographics.growth_12m_pct)]},
                        {label: 'Churn (12 mo)', value: formatPct(firmographics.churn_12m_pct),
                            tone: (firmographics.churn_12m_pct ?? 0) >= CHURN_WARN_PCT ? 'warn' : 'neutral'},
                        {label: 'Active job postings', value: formatCount(firmographics.active_job_postings)},
                        {label: 'Share of revenue', value: share ? formatShare(share) : NO_VALUE,
                            hint: share ? BAND_LABEL[share.band] : undefined,
                            tone: share?.band === 'critical_leverage' ? 'bad' : 'neutral'},
                    ]}/>
                    <SnapshotMeta retrievedAt={firmographics.retrieved_at}/>
                </>
            )}
        </Stack>
    );
};
